import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  private timezone: string | null = null;
  private isInitialized = false;

  constructor() {
    this.detectTimezone();
  }

  /**
   * Detecta la zona horaria del navegador usando la API de Intl
   * @returns La zona horaria en formato IANA o null si no se puede detectar
   */
  detectTimezone(): string | null {
    if (this.isInitialized) {
      return this.timezone;
    }
    this.isInitialized = true;

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone && this.isValidIANATimezone(timezone)) {
        this.timezone = timezone;
        return timezone;
      }

      this.timezone = null;
      return null;
    } catch (error) {
      console.error('Error detectando zona horaria:', error);
      this.timezone = null;
      return null;
    }
  }

  /**
   * Valida que la zona horaria esté en formato IANA (Region/City)
   * @param timezone - Zona horaria a validar
   * @returns true si es una zona IANA válida
   */
  private isValidIANATimezone(timezone: string): boolean {
    const ianaPattern = /^[A-Za-z]+\/[A-Za-z_\-]+(?:\/[A-Za-z_\-]+)?$/;
    if (!ianaPattern.test(timezone)) {
      return false;
    }

    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone });
      return true;
    } catch (error) {
      console.warn(`Zona horaria IANA inválida: ${timezone}`);
      return false;
    }
  }

  /**
   * Obtiene la zona horaria detectada
   * @returns La zona horaria en formato IANA o null si no está disponible
   */
  getTimezone(): string | null {
    if (!this.isInitialized) {
      return this.detectTimezone();
    }
    return this.timezone;
  }

  /**
   * Verifica si la zona horaria está disponible
   */
  hasTimezone(): boolean {
    return this.getTimezone() !== null;
  }

  /**
   * Formatea la zona horaria para el header
   * @returns El valor del header X-Timezone o null si no está disponible
   */
  getTimezoneHeader(): string | null {
    const timezone = this.getTimezone();

    if (timezone && this.isValidIANATimezone(timezone)) {
      return timezone;
    }

    return null;
  }
}
